import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Sidebar.css';
import ContactForm from './ContactForm';
import UpgradeModal from './UpgradeModal';
import SidebarProjectList from './SidebarProjectList';
import { hasAdminAccess } from '../utils/adminUtils';
import { getFeaturePath, parseFeatureProjectFromPath } from '../utils/routeUtils';
import { fetchProjects, setCurrentProject } from '../redux/projectsSlice';
import { resetSessionReduxState } from '../redux/sessionReset';
import { fetchDocumentCategories } from '../redux/documentsSlice';

/* Icons: 9-dot grid, person, document/report, document, document+check, list, code, gear, target */
const IconGrid = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="5" height="5" rx="0.5" />
    <rect x="9.5" y="3" width="5" height="5" rx="0.5" />
    <rect x="16" y="3" width="5" height="5" rx="0.5" />
    <rect x="3" y="9.5" width="5" height="5" rx="0.5" />
    <rect x="9.5" y="9.5" width="5" height="5" rx="0.5" />
    <rect x="16" y="9.5" width="5" height="5" rx="0.5" />
    <rect x="3" y="16" width="5" height="5" rx="0.5" />
    <rect x="9.5" y="16" width="5" height="5" rx="0.5" />
    <rect x="16" y="16" width="5" height="5" rx="0.5" />
  </svg>
);
const IconDoc = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10,9 9,9 8,9" />
  </svg>
);
const IconDocCheck = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14,2 14,8 20,8" />
    <path d="M9 15l2 2 4-4" />
  </svg>
);
const IconList = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);
const IconCode = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16,18 22,12 16,6" />
    <polyline points="8,6 2,12 8,18" />
  </svg>
);
const IconGear = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const IconTarget = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);
const IconLogout = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const Sidebar = ({ setIsAuthenticated, userProfile }) => {
  const dispatch = useDispatch();
  const { allProjects = [], currentProject } = useSelector((state) => state.projects);
  const { categories = [] } = useSelector((state) => state.documents);
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [primaryActive, setPrimaryActive] = useState('project');
  const [, setHasApiKey] = useState(false);
  const [, setApiKeyChecked] = useState(false);
  const [trialPeriodEndDate, setTrialPeriodEndDate] = useState(null);
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [trialTokenLimit, setTrialTokenLimit] = useState(3000000);
  const [userPlan, setUserPlan] = useState('trial');
  const [tokenLimit, setTokenLimit] = useState(3000000);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState('warning');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  // Convert Redux projects to sidebar format
  const sidebarProjects = allProjects.map((p) => ({
    id: String(p.id),
    name: p.projectName,
    brief: p.brief,
  }));

  // Refetch projects whenever the authenticated sidebar mounts (avoids showing another user's cached list)
  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(fetchDocumentCategories());
    }
  }, [dispatch, categories.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const checkApiKeyStatus = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API_BASE_URL}/api/user/openai-key`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data && response.data.success !== false) {
            setHasApiKey(response.data.hasApiKey);
          } else {
            setHasApiKey(false);
          }
        }
        setApiKeyChecked(true);
      } catch (error) {
        console.error('Error checking API key status:', error);
        setHasApiKey(false);
        setApiKeyChecked(true);
      }
    };

    const checkTrialPeriod = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            if (response.data.trialPeriodEndDate) setTrialPeriodEndDate(response.data.trialPeriodEndDate);
            if (response.data.totalTokensUsed !== undefined) setTotalTokensUsed(response.data.totalTokensUsed);
            if (response.data.plan) setUserPlan(response.data.plan);
            if (response.data.tokenLimit !== undefined) setTokenLimit(response.data.tokenLimit || 3000000);
            if (response.data.trialTokenLimit !== undefined) setTrialTokenLimit(response.data.trialTokenLimit);
          }
        }
      } catch (error) {
        console.error('Error checking trial period:', error);
      }
    };

    checkApiKeyStatus();
    checkTrialPeriod();
  }, []);

  useEffect(() => {
    document.body.classList.toggle('sidebar-hidden', !isSidebarVisible);
    return () => document.body.classList.remove('sidebar-hidden');
  }, [isSidebarVisible]);

  useEffect(() => {
    const mobileNavOpen =
      typeof window !== 'undefined' &&
      window.innerWidth <= 768 &&
      navOpen &&
      isSidebarVisible;
    document.body.classList.toggle('sidebar-nav-open', mobileNavOpen);
    return () => document.body.classList.remove('sidebar-nav-open');
  }, [navOpen, isSidebarVisible]);

  // Update primaryActive based on current route (including /feature/projectId/:id)
  useEffect(() => {
    const currentPath = location.pathname;
    const { featureBase } = parseFeatureProjectFromPath(currentPath);
    if (featureBase) {
      setPrimaryActive(featureBase);
    } else if (currentPath.startsWith('/codelens-qa')) {
      setPrimaryActive('/codelens-qa');
    } else if (currentPath === '/home' || currentPath.startsWith('/project/')) {
      setPrimaryActive('project');
    }
  }, [location.pathname]);

  const featureRouteContext = parseFeatureProjectFromPath(location.pathname);
  const isCodelensRoute = location.pathname.startsWith('/codelens-qa');
  const showProjectSecondaryNav =
    primaryActive === 'project' || featureRouteContext.featureBase != null;
  const showCodelensSecondaryNav = isCodelensRoute;

  /** Active project for AI features: URL (feature or project page) first, then Redux session selection */
  const getResolvedProjectIdForFeatures = () => {
    const { projectId: fromFeature } = parseFeatureProjectFromPath(location.pathname);
    if (fromFeature != null && String(fromFeature).trim() !== '') {
      return String(fromFeature);
    }
    const projectPageMatch = location.pathname.match(/^\/project\/([^/]+)/);
    if (projectPageMatch) {
      return projectPageMatch[1];
    }
    if (currentProject?.id != null && String(currentProject.id).trim() !== '') {
      return String(currentProject.id);
    }
    return null;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    resetSessionReduxState();
    setIsAuthenticated(false);
    navigate('/login');
  };

  const handleNavClick = (path, e) => {
    const aiFeatures = ['/brd-to-frd', '/generate-test-cases', '/auto-test-data', '/automation-scripts', '/generate-bug-report', '/rest-assured-api-testing'];
    if (aiFeatures.includes(path)) {
      const currentLimit = userPlan === 'trial' ? trialTokenLimit : (userPlan === 'free' ? 1000000 : (userPlan === 'paid' ? tokenLimit : null));
      if (currentLimit !== null && totalTokensUsed >= currentLimit) {
        e.preventDefault();
        const planName = userPlan === 'trial' ? 'trial period' : (userPlan === 'free' ? 'free plan' : 'plan');
        setAlertMessage(`Your ${planName} token limit of ${currentLimit.toLocaleString()} tokens has been reached. Please contact support to continue using the service.`);
        setAlertVariant('warning');
        setTimeout(() => { setAlertMessage(null); navigate('/profile'); }, 3000);
        return;
      }
    }
  };

  const getUserInitials = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return (userProfile.firstName.charAt(0) + userProfile.lastName.charAt(0)).toUpperCase();
    }
    return 'U';
  };

  const getUserRole = () => userProfile?.role || 'User';

  const getFullDisplayName = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      const lastInitial = userProfile.lastName.charAt(0).toUpperCase();
      return `${userProfile.firstName} ${lastInitial}`;
    }
    return userProfile?.firstName || userProfile?.username || 'User';
  };

  const getWelcomeName = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    return userProfile?.firstName || userProfile?.username || 'User';
  };

  const getWelcomeDisplayText = () => {
    // const projectMatch = location.pathname.match(/^\/project\/([^/]+)$/);
    // if (projectMatch) {
    //   const projectId = projectMatch[1];
    //   const project = sidebarProjects.find((p) => p.id === projectId);
    //   if (project?.name) return project.name;
    // }
    return getWelcomeName();
  };

  const getTrialDaysLeft = () => {
    if (!trialPeriodEndDate || userPlan !== 'trial') return null;
    const end = new Date(trialPeriodEndDate);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const getRoleLabel = () => {
    const role = getUserRole();
    if (hasAdminAccess(role)) return 'Admin Account';
    return 'User Account';
  };

  const handleProjectConfirm = () => {
    if (!selectedProjectId || !selectedFeature) return;
    const path = selectedFeature;
    const aiFeatures = ['/brd-to-frd', '/generate-test-cases', '/auto-test-data', '/automation-scripts', '/generate-bug-report', '/rest-assured-api-testing'];
    if (aiFeatures.includes(path)) {
      const currentLimit = userPlan === 'trial' ? trialTokenLimit : (userPlan === 'free' ? 1000000 : (userPlan === 'paid' ? tokenLimit : null));
      if (currentLimit !== null && totalTokensUsed >= currentLimit) {
        const planName = userPlan === 'trial' ? 'trial period' : (userPlan === 'free' ? 'free plan' : 'plan');
        setAlertMessage(`Your ${planName} token limit of ${currentLimit.toLocaleString()} tokens has been reached. Please contact support to continue using the service.`);
        setAlertVariant('warning');
        setTimeout(() => setAlertMessage(null), 3000);
        setShowProjectModal(false);
        setSelectedFeature(null);
        setSelectedProjectId(null);
        navigate('/profile');
        return;
      }
    }
    const fullProject = allProjects.find((p) => String(p.id) === String(selectedProjectId));
    if (fullProject) {
      dispatch(setCurrentProject(fullProject));
    }
    navigate(getFeaturePath(path, selectedProjectId));
    setShowProjectModal(false);
    setSelectedFeature(null);
    setSelectedProjectId(null);
    setNavOpen(false);
  };

  const handleSidebarToggle = () => {
    if (window.innerWidth <= 768) {
      setNavOpen(!navOpen);
      return;
    }
    setIsSidebarVisible(!isSidebarVisible);
  };

  const primaryLinkItems = [
    { path: '/brd-to-frd', label: 'BRD to FRD', icon: <IconDoc /> },
    { path: '/generate-test-cases', label: 'Test Cases', icon: <IconDocCheck /> },
    { path: '/auto-test-data', label: 'Auto Test Data', icon: <IconList /> },
    { path: '/automation-scripts', label: 'Automation Scripts', icon: <IconCode /> },
    { path: '/generate-bug-report', label: 'Bug Report', icon: <IconGear /> },
    { path: '/rest-assured-api-testing', label: 'API Test Automation', icon: <IconTarget /> },
    { path: '/codelens-qa', label: 'CodeLens QA', icon: <IconCode />, direct: true },
  ];

  return (
    <>
      {alertMessage && (
        <Alert variant={alertVariant} dismissible onClose={() => setAlertMessage(null)} className="sidebar-alert" style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 9999, maxWidth: '400px' }}>
          {alertMessage}
        </Alert>
      )}
      <header className={`sidebar-app-header ${isSidebarVisible ? '' : 'sidebar-collapsed'}`}>
        <div className={`sidebar-header-logo-strip ${isSidebarVisible ? '' : 'sidebar-collapsed'}`}>
          <button
            type="button"
            className="sidebar-header-hamburger"
            onClick={handleSidebarToggle}
            aria-expanded={window.innerWidth <= 768 ? navOpen : isSidebarVisible}
            aria-label={window.innerWidth <= 768 ? (navOpen ? 'Close menu' : 'Open menu') : (isSidebarVisible ? 'Hide sidebar' : 'Show sidebar')}
          >
            <span className="sidebar-header-hamburger-icon" aria-hidden="true"><span /><span /><span /></span>
          </button>
          <div className="sidebar-logo-block">
            <img src="/header-logo.png" alt="" className="sidebar-logo-icon" />
          </div>
        </div>
        <div className="sidebar-header-main">
          <p className="sidebar-welcome">{getWelcomeDisplayText()}!</p>
          <div className="sidebar-header-actions">
            {userPlan === 'trial' && getTrialDaysLeft() !== null && (
              <span className="sidebar-trial-text">
                Trial Period: {getTrialDaysLeft()} day{getTrialDaysLeft() !== 1 ? 's' : ''} left
              </span>
            )}
            {(userPlan === 'free' || userPlan === 'trial') && (
              <button type="button" className="sidebar-upgrade-btn" onClick={() => setShowUpgradeModal(true)}>
                UPGRADE
              </button>
            )}
            <div className="sidebar-header-user-wrap" ref={userMenuRef}>
              <button type="button" className="sidebar-user-trigger" onClick={() => setUserMenuOpen(!userMenuOpen)} aria-expanded={userMenuOpen} aria-haspopup="true">
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{getFullDisplayName()}</span>
                  <span className="sidebar-user-role">{getRoleLabel()}</span>
                </div>
                <div className="sidebar-avatar">
                  {userProfile?.profilePhoto ? <img src={userProfile.profilePhoto} alt="" /> : <span>{getUserInitials()}</span>}
                </div>
                <svg className="sidebar-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6,9 12,15 18,9" /></svg>
              </button>
              {userMenuOpen && (
                <div className="sidebar-dropdown">
                  <NavLink to="/profile" className="sidebar-dropdown-item" onClick={() => setUserMenuOpen(false)}>Profile</NavLink>
                  <button type="button" className="sidebar-dropdown-item" onClick={() => { setUserMenuOpen(false); setShowContactForm(true); }}>Contact</button>
                  {hasAdminAccess(getUserRole()) && <NavLink to="/admin" className="sidebar-dropdown-item" onClick={() => setUserMenuOpen(false)}>Admin</NavLink>}
                  <button type="button" className="sidebar-dropdown-item sidebar-dropdown-logout" onClick={() => { setUserMenuOpen(false); handleLogout(); }}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className={`sidebar sidebar-vertical ${navOpen ? 'nav-open' : ''} ${isSidebarVisible ? '' : 'sidebar-hidden'}`}>
        <div className="sidebar-body">
          <nav className="sidebar-primary-nav" aria-label="Primary">
            <button
              type="button"
              className={`sidebar-primary-item ${primaryActive === 'project' ? 'active' : ''}`}
              onClick={() => setPrimaryActive('project')}
            >
              <span className="sidebar-primary-icon"><IconGrid /></span>
              <span className="sidebar-primary-label">Projects</span>
            </button>
            {primaryLinkItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`sidebar-primary-link ${primaryActive === item.path ? 'active' : ''}`}
                onClick={() => {
                  const path = item.path;
                  if (item.direct) {
                    setPrimaryActive(path);
                    navigate(path);
                    setNavOpen(false);
                    return;
                  }
                  const aiFeatures = ['/brd-to-frd', '/generate-test-cases', '/auto-test-data', '/automation-scripts', '/generate-bug-report', '/rest-assured-api-testing'];
                  if (aiFeatures.includes(path)) {
                    const currentLimit = userPlan === 'trial' ? trialTokenLimit : (userPlan === 'free' ? 1000000 : (userPlan === 'paid' ? tokenLimit : null));
                    if (currentLimit !== null && totalTokensUsed >= currentLimit) {
                      const planName = userPlan === 'trial' ? 'trial period' : (userPlan === 'free' ? 'free plan' : 'plan');
                      setAlertMessage(`Your ${planName} token limit of ${currentLimit.toLocaleString()} tokens has been reached. Please contact support to continue using the service.`);
                      setAlertVariant('warning');
                      setTimeout(() => setAlertMessage(null), 3000);
                      navigate('/profile');
                      return;
                    }
                  }

                  const resolvedProjectId = getResolvedProjectIdForFeatures();
                  if (resolvedProjectId) {
                    navigate(getFeaturePath(path, resolvedProjectId));
                    setNavOpen(false);
                    return;
                  }

                  setSelectedProjectId(null);
                  setSelectedFeature(path);
                  setShowProjectModal(true);
                }}
              >
                <span className="sidebar-primary-icon">{item.icon}</span>
                <span className="sidebar-primary-label">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="sidebar-divider" aria-hidden="true" />

          <nav className="sidebar-secondary-nav" aria-label="Secondary">
            {showCodelensSecondaryNav && (
              <>
                <NavLink
                  to="/codelens-qa"
                  className={({ isActive }) =>
                    `sidebar-secondary-link ${isActive && location.pathname === '/codelens-qa' ? 'active' : ''}`
                  }
                  onClick={() => setNavOpen(false)}
                >
                  <span className="sidebar-secondary-icon"><IconCode /></span>
                  <span className="sidebar-secondary-label">New workflow</span>
                </NavLink>
                <NavLink
                  to="/codelens-qa/history"
                  className={({ isActive }) =>
                    `sidebar-secondary-link ${isActive ? 'active' : ''}`
                  }
                  onClick={() => setNavOpen(false)}
                >
                  <span className="sidebar-secondary-icon"><IconDocCheck /></span>
                  <span className="sidebar-secondary-label">Previous workflows</span>
                </NavLink>
              </>
            )}
            {showProjectSecondaryNav && !showCodelensSecondaryNav && (
              <>
                <NavLink
                  to="/home"
                  className={({ isActive }) => `sidebar-secondary-link ${isActive ? 'active' : ''}`}
                  onClick={(e) => { handleNavClick('/home', e); setNavOpen(false); }}
                >
                  <span className="sidebar-secondary-icon"><IconGrid /></span>
                  <span className="sidebar-secondary-label">Projects</span>
                </NavLink>
                <button
                  type="button"
                  className="sidebar-secondary-create-btn"
                  onClick={() => {
                    navigate('/home', { state: { openAddProject: true } });
                    setNavOpen(false);
                  }}
                >
                  Add Project
                </button>
                <SidebarProjectList projects={sidebarProjects} onItemClick={() => setNavOpen(false)} />
              </>
            )}
          </nav>
        </div>

        <footer className="sidebar-footer">
          <button type="button" className="sidebar-footer-logout" onClick={handleLogout} aria-label="Log out">
            <span className="sidebar-footer-logout-icon" aria-hidden="true"><IconLogout /></span>
            <span className="sidebar-footer-logout-label">Logout</span>
          </button>
        </footer>

        <button type="button" className="sidebar-mobile-toggle" onClick={() => setNavOpen(!navOpen)} aria-expanded={navOpen} aria-label={navOpen ? 'Close menu' : 'Open menu'}>
          <span className="sidebar-mobile-toggle-icon"><span /><span /><span /></span>
        </button>
      </div>
      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      {showProjectModal && (
        <div className="sidebar-project-picker-backdrop" onClick={() => { setShowProjectModal(false); setSelectedFeature(null); setSelectedProjectId(null); }} aria-hidden="true" />
      )}
      {showProjectModal && (
        <div className="sidebar-project-picker-modal" role="dialog" aria-modal="true" aria-labelledby="sidebar-project-picker-title">
          <h2 id="sidebar-project-picker-title" className="sidebar-project-picker-title">
            Select a project for {primaryLinkItems.find(item => item.path === selectedFeature)?.label || 'this feature'}
          </h2>
          <ul className="sidebar-project-picker-list">
            {sidebarProjects.map((proj) => (
              <li key={proj.id}>
                <button
                  type="button"
                  className={`sidebar-project-picker-item ${selectedProjectId === proj.id ? 'selected' : ''}`}
                  onClick={() => setSelectedProjectId(proj.id)}
                >
                  <span className="sidebar-project-picker-item-name">{proj.name}</span>
                  {proj.brief && <span className="sidebar-project-picker-item-brief">{proj.brief}</span>}
                </button>
              </li>
            ))}
          </ul>
          <div className="sidebar-project-picker-actions">
            <button
              type="button"
              className="sidebar-project-picker-add"
              onClick={() => {
                setShowProjectModal(false);
                setSelectedFeature(null);
                setSelectedProjectId(null);
                navigate('/home', { state: { openAddProject: true } });
              }}
            >
              Add Project
            </button>
            <button type="button" className="sidebar-project-picker-cancel" onClick={() => { setShowProjectModal(false); setSelectedFeature(null); setSelectedProjectId(null); }}>Cancel</button>
            <button type="button" className="sidebar-project-picker-ok" onClick={handleProjectConfirm} disabled={!selectedProjectId}>OK</button>
          </div>
        </div>
      )}

      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} userPlan={userPlan} />
      <ContactForm open={showContactForm} onClose={() => setShowContactForm(false)} />
    </>
  );
};
export default Sidebar;
